import React, { useCallback, useRef, useEffect } from "react";
import { Marker } from "react-map-gl/mapbox";
import { motion, AnimatePresence } from "framer-motion";
import { AvatarFill } from "./AvatarMarker";
import { logError } from "../../utils/logger";

// ============================================================================
// CONFIGURATION
// ============================================================================

const PRIMARY = "var(--clr-primary-default)";

// Collapsed state — matches ClusterMarker dimensions
const COLLAPSED_AVATAR_SIZE = 34;
const COLLAPSED_AVATAR_OVERLAP = -28;
const PILL_PADDING = 2;
const PILL_BORDER = 1.5;

// Expanded state — matches AvatarMarker size for consistency
const EXPANDED_AVATAR_SIZE = 36;
const EXPANDED_AVATAR_GAP = 8;
const EXPANDED_PILL_PADDING = 4;

// Max visible avatars before showing "+N" overflow
const MAX_VISIBLE_EXPANDED = 3;

// Shared stem + tip
const STEM_WIDTH = 2;
const STEM_HEIGHT = 12;
const TIP_SIZE = 4;

// Stacked shadow offset for co-location hint
const STACK_OFFSET = 3;

// ============================================================================
// COLLAPSED PILL — renders like ClusterMarker with a stacked-shadow hint
// ============================================================================

function CollapsedPill({ avatars, count, allViewed, color, pillBg, isDark }) {
  const displayAvatars = avatars.slice(0, 3);
  const displayCount = count > 99 ? "99+" : String(count);
  const badgeSize = displayCount.length > 2 ? 24 : 20;

  return (
    <div style={{ position: "relative" }}>
      {/* Stacked shadow layer — offset copy behind the pill to hint "multiple at same spot" */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          top: STACK_OFFSET,
          left: STACK_OFFSET,
          background: pillBg,
          border: `${PILL_BORDER}px solid ${allViewed ? (isDark ? "#4b5563" : "#9ca3af") : "var(--clr-primary-400)"}`,
          borderRadius: 999,
          opacity: 0.45,
        }}
      />

      {/* Main pill */}
      <div
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          background: pillBg,
          border: `${PILL_BORDER}px solid ${color}`,
          borderRadius: 999,
          padding: PILL_PADDING,
        }}
      >
        {displayAvatars.map((av, i) => (
          <div
            key={i}
            className="rounded-full overflow-hidden flex-shrink-0"
            style={{
              width: COLLAPSED_AVATAR_SIZE,
              height: COLLAPSED_AVATAR_SIZE,
              border: `1px solid ${allViewed ? color : "var(--clr-primary-600)"}`,
              marginLeft: i === 0 ? 0 : COLLAPSED_AVATAR_OVERLAP,
              position: "relative",
              zIndex: displayAvatars.length - i,
              filter: allViewed ? "grayscale(100%)" : "none",
            }}
          >
            <AvatarFill src={av.avatar} name={av.name} size={COLLAPSED_AVATAR_SIZE} />
          </div>
        ))}

        {/* Count badge */}
        <div
          className="absolute rounded-full flex items-center justify-center"
          style={{
            width: badgeSize,
            height: badgeSize,
            backgroundColor: color,
            top: -6,
            right: -6,
            zIndex: 10,
            border: `1.5px solid ${isDark ? pillBg : "white"}`,
          }}
        >
          <span
            className="font-bold text-white leading-none"
            style={{ fontSize: displayCount.length > 2 ? 9 : 10 }}
          >
            {displayCount}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EXPANDED PILL — individual tappable avatars fanned out horizontally
// ============================================================================

function ExpandedPill({ notes, allViewed, color, pillBg, isDark, onNoteSelect }) {
  const visibleNotes = notes.slice(0, MAX_VISIBLE_EXPANDED);
  const overflowCount = notes.length - MAX_VISIBLE_EXPANDED;
  const scrollContainerRef = useRef(null);

  // For 4+ notes, enable horizontal scroll with all notes visible
  const showScroll = notes.length > MAX_VISIBLE_EXPANDED;
  const displayNotes = showScroll ? notes : visibleNotes;

  const handleNoteClick = useCallback(
    (e, note) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        onNoteSelect?.(note);
      } catch (error) {
        logError("Error selecting co-located note:", error);
      }
    },
    [onNoteSelect],
  );

  return (
    <motion.div
      initial={{ opacity: 0, scaleX: 0.6, scaleY: 0.85 }}
      animate={{ opacity: 1, scaleX: 1, scaleY: 1 }}
      exit={{ opacity: 0, scaleX: 0.6, scaleY: 0.85 }}
      transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        background: pillBg,
        border: `${PILL_BORDER}px solid ${color}`,
        borderRadius: 999,
        padding: EXPANDED_PILL_PADDING,
        transformOrigin: "center bottom",
        maxWidth: showScroll ? `${MAX_VISIBLE_EXPANDED * (EXPANDED_AVATAR_SIZE + EXPANDED_AVATAR_GAP) + EXPANDED_AVATAR_SIZE + EXPANDED_PILL_PADDING * 2}px` : "none",
      }}
    >
      <div
        ref={scrollContainerRef}
        style={{
          display: "flex",
          alignItems: "center",
          gap: EXPANDED_AVATAR_GAP,
          overflowX: showScroll ? "auto" : "visible",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          // Hide scrollbar via inline for webkit
        }}
        className="[&::-webkit-scrollbar]:hidden"
      >
        {displayNotes.map((note, i) => {
          const isViewed = note?.isOwner || note?.isViewedByCurrentUser;
          const MUTED = isDark ? "#4b5563" : "#6b7280";
          const avatarBorder = isViewed ? MUTED : PRIMARY;

          return (
            <motion.button
              key={note.id}
              type="button"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.04, duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => handleNoteClick(e, note)}
              aria-label={`Open note by ${note?.owner?.name || "unknown"}`}
              title={note?.owner?.name || "Note"}
              className="relative flex-shrink-0 focus:outline-none rounded-full"
              style={{
                width: EXPANDED_AVATAR_SIZE,
                height: EXPANDED_AVATAR_SIZE,
                transition: "transform 120ms, box-shadow 120ms",
              }}
              whileHover={{ scale: 1.12 }}
              whileTap={{ scale: 0.95 }}
            >
              <div
                className="rounded-full overflow-hidden w-full h-full"
                style={{
                  border: `2px solid ${avatarBorder}`,
                  filter: isViewed ? "grayscale(100%)" : "none",
                  opacity: isViewed ? 0.65 : 1,
                }}
              >
                <AvatarFill
                  src={note?.owner?.avatar}
                  name={note?.owner?.name}
                  size={EXPANDED_AVATAR_SIZE}
                />
              </div>

            </motion.button>
          );
        })}

        {/* Overflow indicator — only shown when not scrollable (fallback) */}
        {!showScroll && overflowCount > 0 && (
          <div
            className="flex-shrink-0 rounded-full flex items-center justify-center"
            style={{
              width: EXPANDED_AVATAR_SIZE,
              height: EXPANDED_AVATAR_SIZE,
              backgroundColor: isDark ? "#374151" : "#d1d5db",
              border: `2px solid ${color}`,
              fontSize: 11,
              fontWeight: 700,
              color: isDark ? "#e5e7eb" : "#374151",
            }}
          >
            +{overflowCount}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ColocatedMarker — Handles multiple notes at the same geographic location.
 *
 * Collapsed: looks like a ClusterMarker with a stacked-shadow depth cue.
 * Expanded: fans out into individually tappable avatars in a horizontal row.
 */
export default function ColocatedMarker({
  cluster,
  isExpanded,
  onExpand,
  onCollapse,
  onNoteSelect,
  zIndex,
}) {
  const { longitude, latitude, count, notes = [], avatars = [], allViewed = false } = cluster;
  const markerRef = useRef(null);

  const isDark = document.documentElement.classList.contains("dark");
  const MUTED = isDark ? "#4b5563" : "#6b7280";
  const color = allViewed ? MUTED : PRIMARY;
  const containerOpacity = allViewed ? 0.65 : 1;
  const pillBg = isDark ? "#1e2030" : "#e5e7eb";

  // -------------------------------------------------------------------------
  // Dismiss: map click (not on this marker) collapses the expanded state.
  // We listen on the map canvas directly so modal backdrops (portals outside
  // the map DOM) don't accidentally trigger a collapse.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isExpanded) return;

    // Find the Mapbox canvas — it's the sibling/parent of the marker overlays
    const mapCanvas = markerRef.current?.closest(".mapboxgl-map")?.querySelector(".mapboxgl-canvas");
    if (!mapCanvas) return;

    const handleMapClick = () => {
      onCollapse?.();
    };

    // Small delay so the expand-click doesn't immediately trigger collapse
    const timer = setTimeout(() => {
      mapCanvas.addEventListener("click", handleMapClick);
    }, 50);

    return () => {
      clearTimeout(timer);
      mapCanvas.removeEventListener("click", handleMapClick);
    };
  }, [isExpanded, onCollapse]);

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  const handleCollapsedClick = useCallback(
    (e) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        onExpand?.();
      } catch (error) {
        logError("Error expanding co-located marker:", error);
      }
    },
    [onExpand],
  );

  const handleNoteSelected = useCallback(
    (note) => {
      // Don't collapse — keep expanded so user can pick another note after closing the modal
      onNoteSelect?.(note);
    },
    [onNoteSelect],
  );

  const handleMouseEnter = (e) => {
    try {
      if (e.currentTarget && !isExpanded) {
        e.currentTarget.style.transform = "translateY(-2px)";
      }
    } catch (error) {
      logError("Error handling mouse enter:", error);
    }
  };

  const handleMouseLeave = (e) => {
    try {
      if (e.currentTarget && !isExpanded) {
        e.currentTarget.style.transform = "translateY(0px)";
      }
    } catch (error) {
      logError("Error handling mouse leave:", error);
    }
  };

  const handleMouseDown = (e) => {
    try {
      if (e.currentTarget && !isExpanded) {
        e.currentTarget.style.transform = "translateY(0px)";
      }
    } catch (error) {
      logError("Error handling mouse down:", error);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      anchor="bottom"
      style={zIndex != null ? { zIndex } : undefined}
    >
      <div
        ref={markerRef}
        role="button"
        tabIndex={0}
        onClick={isExpanded ? undefined : handleCollapsedClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (isExpanded) onCollapse?.();
            else onExpand?.();
          }
          if (e.key === "Escape" && isExpanded) {
            onCollapse?.();
          }
        }}
        aria-label={
          isExpanded
            ? `${count} notes at this location, expanded. Press Escape to collapse.`
            : `${count} notes at this location. Press Enter to expand.`
        }
        className="group relative focus:outline-none"
        style={{
          transform: "translateY(0px)",
          transition: "transform 120ms",
          animation: isExpanded ? undefined : "markerPopIn 200ms ease-out",
          cursor: isExpanded ? "default" : "pointer",
          // Block touch events from reaching the map when expanded
          touchAction: isExpanded ? "pan-x" : undefined,
        }}
        // When expanded, stop ALL pointer/touch events from propagating to the map
        // This prevents map dragging when user scrolls the avatar row
        onTouchStart={isExpanded ? (e) => e.stopPropagation() : undefined}
        onTouchMove={isExpanded ? (e) => e.stopPropagation() : undefined}
        onTouchEnd={isExpanded ? (e) => e.stopPropagation() : undefined}
        onPointerDown={isExpanded ? (e) => e.stopPropagation() : undefined}
        onPointerMove={isExpanded ? (e) => e.stopPropagation() : undefined}
        onPointerUp={isExpanded ? (e) => e.stopPropagation() : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={isExpanded ? (e) => e.stopPropagation() : handleMouseDown}
      >
        <div className="flex flex-col items-center" style={{ opacity: containerOpacity }}>
          <AnimatePresence mode="wait">
            {isExpanded ? (
              <ExpandedPill
                key="expanded"
                notes={notes}
                allViewed={allViewed}
                color={color}
                pillBg={pillBg}
                isDark={isDark}
                onNoteSelect={handleNoteSelected}
              />
            ) : (
              <motion.div
                key="collapsed"
                initial={false}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.12 }}
              >
                <CollapsedPill
                  avatars={avatars}
                  count={count}
                  allViewed={allViewed}
                  color={color}
                  pillBg={pillBg}
                  isDark={isDark}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stem */}
          <div
            aria-hidden="true"
            style={{
              width: STEM_WIDTH,
              height: STEM_HEIGHT,
              background: `linear-gradient(to bottom, ${color}, transparent)`,
            }}
          />

          {/* Tip dot */}
          <div
            aria-hidden="true"
            className="rounded-full"
            style={{
              width: TIP_SIZE,
              height: TIP_SIZE,
              backgroundColor: allViewed ? MUTED : "var(--clr-primary-400)",
              opacity: 0.5,
            }}
          />
        </div>
      </div>
    </Marker>
  );
}
